/**
 * Cached Query Hooks (Day-15 Module 11 - Performance Optimization)
 * 
 * Custom hooks using TanStack Query for:
 * - My Shifts
 * - Participant Profile
 * - Medication Chart
 * - House Logs
 * 
 * Cache rules:
 * - staleTime: 2 minutes
 * - cacheTime: 5 minutes
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys, STALE_TIME, GC_TIME } from '@/lib/queryProvider';

// ─────────────────────────────────────────────────────────────────
// MY SHIFTS HOOKS
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch user's shifts with caching
 * @param {Object} filters - { startDate, endDate, status }
 */
export function useMyShifts(filters = {}) {
  return useQuery({
    queryKey: queryKeys.shifts.myShifts(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      
      const response = await api.get(`/roster/my-shifts?${params.toString()}`);
      return response.data.items || response.data.data || [];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Fetch today's shifts for mobile dashboard
 */
export function useTodayShifts() {
  const today = new Date().toISOString().split('T')[0];
  return useMyShifts({ startDate: today, endDate: today });
}

/**
 * Fetch upcoming shifts (next 7 days)
 */
export function useUpcomingShifts() {
  const today = new Date();
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);
  
  return useMyShifts({
    startDate: today.toISOString().split('T')[0],
    endDate: nextWeek.toISOString().split('T')[0],
  });
}

/**
 * Fetch shift details
 */
export function useShiftDetail(shiftId) {
  return useQuery({
    queryKey: queryKeys.shifts.detail(shiftId),
    queryFn: async () => {
      const response = await api.get(`/roster/shifts/${shiftId}`);
      return response.data.data || response.data;
    },
    enabled: !!shiftId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}


// ─────────────────────────────────────────────────────────────────
// PARTICIPANT PROFILE HOOKS
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch participant profile with caching
 */
export function useParticipantProfile(participantId) {
  return useQuery({
    queryKey: queryKeys.participants.profile(participantId),
    queryFn: async () => {
      const response = await api.get(`/participants/${participantId}`);
      return response.data.data || response.data;
    },
    enabled: !!participantId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Fetch participant list with filters and caching
 */
export function useParticipantList(filters = {}) {
  return useQuery({
    queryKey: queryKeys.participants.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.branchId) params.append('branchId', filters.branchId);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('pageSize', filters.pageSize);
      
      const response = await api.get(`/participants?${params.toString()}`);
      return response.data;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    keepPreviousData: true, // Good for pagination
  });
}

/**
 * Fetch participant care plan
 */
export function useParticipantCarePlan(participantId) {
  return useQuery({
    queryKey: queryKeys.participants.carePlan(participantId),
    queryFn: async () => {
      const response = await api.get(`/participants/${participantId}/care-plan`);
      return response.data.data || response.data;
    },
    enabled: !!participantId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Fetch participant funding summary
 */
export function useParticipantFunding(participantId) {
  return useQuery({
    queryKey: queryKeys.participants.funding(participantId),
    queryFn: async () => {
      const response = await api.get(`/funding/participants/${participantId}/summary`);
      return response.data.data || response.data;
    },
    enabled: !!participantId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}


// ─────────────────────────────────────────────────────────────────
// MEDICATION CHART HOOKS
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch medication chart for a participant
 */
export function useMedicationChart(participantId, filters = {}) {
  return useQuery({
    queryKey: queryKeys.medications.chart(participantId, filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await api.get(`/medications/chart/${participantId}?${params.toString()}`);
      return response.data.data || response.data;
    },
    enabled: !!participantId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Fetch medication schedule for today
 */
export function useMedicationSchedule(participantId) {
  return useQuery({
    queryKey: queryKeys.medications.schedule(participantId),
    queryFn: async () => {
      const response = await api.get(`/medications/schedule/${participantId}`);
      return response.data.data || response.data;
    },
    enabled: !!participantId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Fetch medication administration history
 */
export function useMedicationHistory(participantId, filters = {}) {
  return useQuery({
    queryKey: queryKeys.medications.history(participantId, filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('pageSize', filters.pageSize);
      
      const response = await api.get(`/emar/history/${participantId}?${params.toString()}`);
      return response.data;
    },
    enabled: !!participantId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    keepPreviousData: true,
  });
}


// ─────────────────────────────────────────────────────────────────
// HOUSE LOGS HOOKS
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch house logs with caching
 */
export function useHouseLogs(houseId, filters = {}) {
  return useQuery({
    queryKey: queryKeys.houses.logs(houseId, filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.logType) params.append('logType', filters.logType);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('pageSize', filters.pageSize);
      
      const response = await api.get(`/accommodation/houses/${houseId}/logs?${params.toString()}`);
      return response.data;
    },
    enabled: !!houseId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    keepPreviousData: true,
  });
}

/**
 * Fetch house details with caching
 */
export function useHouseDetail(houseId) {
  return useQuery({
    queryKey: queryKeys.houses.detail(houseId),
    queryFn: async () => {
      const response = await api.get(`/accommodation/houses/${houseId}`);
      return response.data.data || response.data;
    },
    enabled: !!houseId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Fetch house list with filters
 */
export function useHouseList(filters = {}) {
  return useQuery({
    queryKey: queryKeys.houses.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.branchId) params.append('branchId', filters.branchId);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('pageSize', filters.pageSize);
      
      const response = await api.get(`/accommodation/houses?${params.toString()}`);
      return response.data;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    keepPreviousData: true,
  });
}

/**
 * Fetch house rooms
 */
export function useHouseRooms(houseId) {
  return useQuery({
    queryKey: queryKeys.houses.rooms(houseId),
    queryFn: async () => {
      const response = await api.get(`/accommodation/houses/${houseId}/rooms`);
      return response.data.items || response.data.data || [];
    },
    enabled: !!houseId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}


// ─────────────────────────────────────────────────────────────────
// ROSTER QUERY HOOKS (OPTIMIZED)
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch roster with optimized caching
 */
export function useRoster(filters = {}) {
  return useQuery({
    queryKey: queryKeys.shifts.roster(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.staffId) params.append('staffId', filters.staffId);
      if (filters.houseId) params.append('houseId', filters.houseId);
      if (filters.participantId) params.append('participantId', filters.participantId);
      if (filters.status) params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('pageSize', filters.pageSize);
      
      const response = await api.get(`/roster/shifts?${params.toString()}`);
      return response.data;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    keepPreviousData: true,
  });
}


// ─────────────────────────────────────────────────────────────────
// STAFF QUERY HOOKS
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch staff list with caching
 */
export function useStaffList(filters = {}) {
  return useQuery({
    queryKey: queryKeys.staff.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.branchId) params.append('branchId', filters.branchId);
      if (filters.roleId) params.append('roleId', filters.roleId);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('pageSize', filters.pageSize);
      
      const response = await api.get(`/staff?${params.toString()}`);
      return response.data;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    keepPreviousData: true,
  });
}

/**
 * Fetch staff detail
 */
export function useStaffDetail(staffId) {
  return useQuery({
    queryKey: queryKeys.staff.detail(staffId),
    queryFn: async () => {
      const response = await api.get(`/staff/${staffId}`);
      return response.data.data || response.data;
    },
    enabled: !!staffId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}


// ─────────────────────────────────────────────────────────────────
// DASHBOARD HOOKS
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch main dashboard data
 */
export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard.main,
    queryFn: async () => {
      const response = await api.get('/dashboard');
      return response.data.data || response.data;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Fetch mobile dashboard data
 */
export function useMobileDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard.mobile,
    queryFn: async () => {
      const response = await api.get('/mobile/dashboard');
      return response.data.data || response.data;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}


// ─────────────────────────────────────────────────────────────────
// NOTIFICATION HOOKS
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch notifications with caching
 */
export function useNotifications(filters = {}) {
  return useQuery({
    queryKey: queryKeys.notifications.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.filter) params.append('filter', filters.filter);
      if (filters.isRead !== undefined) params.append('isRead', filters.isRead);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('pageSize', filters.pageSize);
      
      const response = await api.get(`/mobile/notifications?${params.toString()}`);
      return response.data;
    },
    staleTime: 30 * 1000, // Notifications update more frequently - 30 seconds
    gcTime: GC_TIME,
    keepPreviousData: true,
  });
}

/**
 * Fetch notification counts
 */
export function useNotificationCounts() {
  return useQuery({
    queryKey: queryKeys.notifications.counts(),
    queryFn: async () => {
      const response = await api.get('/mobile/notifications/counts');
      return response.data.data || response.data;
    },
    staleTime: 30 * 1000, // 30 seconds for counts
    gcTime: GC_TIME,
  });
}


// ─────────────────────────────────────────────────────────────────
// CACHE INVALIDATION HELPERS
// ─────────────────────────────────────────────────────────────────

/**
 * Hook to get query client for cache invalidation
 */
export function useCacheInvalidation() {
  const queryClient = useQueryClient();
  
  return {
    // Invalidate shifts cache
    invalidateShifts: () => queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all }),
    
    // Invalidate participant cache
    invalidateParticipant: (id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.participants.profile(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.participants.carePlan(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.participants.funding(id) });
    },
    invalidateParticipants: () => queryClient.invalidateQueries({ queryKey: queryKeys.participants.all }),
    
    // Invalidate medication cache
    invalidateMedications: (participantId) => {
      if (participantId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.medications.chart(participantId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.medications.schedule(participantId) });
      } else {
        queryClient.invalidateQueries({ queryKey: queryKeys.medications.all });
      }
    },
    
    // Invalidate house cache
    invalidateHouse: (id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.houses.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.houses.logs(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.houses.rooms(id) });
    },
    invalidateHouses: () => queryClient.invalidateQueries({ queryKey: queryKeys.houses.all }),
    
    // Invalidate notifications
    invalidateNotifications: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all }),
    
    // Invalidate dashboard
    invalidateDashboard: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.main }),
    
    // Invalidate all
    invalidateAll: () => queryClient.invalidateQueries(),
  };
}

export default {
  useMyShifts,
  useTodayShifts,
  useUpcomingShifts,
  useShiftDetail,
  useParticipantProfile,
  useParticipantList,
  useParticipantCarePlan,
  useParticipantFunding,
  useMedicationChart,
  useMedicationSchedule,
  useMedicationHistory,
  useHouseLogs,
  useHouseDetail,
  useHouseList,
  useHouseRooms,
  useRoster,
  useStaffList,
  useStaffDetail,
  useDashboard,
  useMobileDashboard,
  useNotifications,
  useNotificationCounts,
  useCacheInvalidation,
};
